import React, { memo, useMemo } from 'react';

interface Props {
	effect: string;
	active: boolean;
}

interface Particle {
	id: number;
	x: number;
	size: number;
	delay: number;
	duration: number;
	color: string;
	char?: string;
	swayAmount: number;
	rotation?: number;
	shape?: string;
}

const EFFECT_CONFIG: Record<string, { colors: string[]; count: number; direction: 'down' | 'up'; chars?: string[]; shape?: string }> = {
	confetti: { colors: ['#FF6F61', '#FFD166', '#06D6A0', '#118AB2', '#EF476F', '#FFD700', '#E040FB'], count: 50, direction: 'down', shape: 'confetti' },
	balloons: { colors: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#DDA0DD', '#98D8C8'], count: 16, direction: 'up', shape: 'balloon' },
	hearts: { colors: ['#E91E63', '#FF4081', '#F48FB1', '#EC407A', '#C2185B'], count: 25, direction: 'up', chars: ['\u2764', '\u2665', '\u2764\uFE0F'] },
	fireworks: { colors: ['#FFD700', '#FF6347', '#00CED1', '#FF69B4', '#7B68EE', '#FF4500', '#00FF7F'], count: 35, direction: 'up', shape: 'firework' },
	snowflakes: { colors: ['#FFFFFF', '#E3F2FD', '#BBDEFB', '#90CAF9'], count: 40, direction: 'down', chars: ['\u2744', '\u2745', '\u2746'] },
	sparkles: { colors: ['#FFD700', '#FFF8DC', '#FFFACD', '#FFE082', '#FFC107'], count: 30, direction: 'up', chars: ['\u2728', '\u2B50', '\u2726', '\u2736'] },
	emojis: { colors: ['#000000'], count: 20, direction: 'up', chars: ['\uD83C\uDF89', '\uD83E\uDD73', '\u2764\uFE0F', '\u2B50', '\uD83C\uDF8A', '\uD83C\uDF88', '\uD83D\uDCAB', '\u2728'] },
};

function generateParticles(effect: string): Particle[] {
	const config = EFFECT_CONFIG[effect];
	if (!config) return [];
	const particles: Particle[] = [];
	for (let i = 0; i < config.count; i++) {
		particles.push({
			id: i,
			x: Math.random() * 100,
			size: effect === 'emojis' ? 20 + Math.random() * 16
				: effect === 'balloons' ? 30 + Math.random() * 20
				: effect === 'hearts' || effect === 'snowflakes' ? 16 + Math.random() * 18
				: effect === 'sparkles' ? 14 + Math.random() * 14
				: 8 + Math.random() * 12,
			delay: Math.random() * 3,
			duration: effect === 'balloons' ? 5 + Math.random() * 4
				: effect === 'snowflakes' ? 4 + Math.random() * 5
				: 2.5 + Math.random() * 3.5,
			color: config.colors[Math.floor(Math.random() * config.colors.length)],
			char: config.chars ? config.chars[Math.floor(Math.random() * config.chars.length)] : undefined,
			swayAmount: effect === 'snowflakes' ? 40 + Math.random() * 80
				: effect === 'balloons' ? 50 + Math.random() * 70
				: 30 + Math.random() * 60,
			rotation: Math.random() * 360,
			shape: config.shape,
		});
	}
	return particles;
}

const BoardEffects: React.FC<Props> = ({ effect, active }) => {
	const particles = useMemo(() => (active && effect && effect !== 'none' ? generateParticles(effect) : []), [effect, active]);
	const config = EFFECT_CONFIG[effect];

	if (!active || !effect || effect === 'none' || !config || particles.length === 0) {
		return null;
	}

	const isDown = config.direction === 'down';

	return (
		<div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 40, overflow: 'hidden' }}>
			<style>{`
				@keyframes boardEffectFall {
					0% { transform: translateY(-10%) translateX(0) rotate(0deg); opacity: 1; }
					25% { transform: translateY(25vh) translateX(calc(var(--sway) * 0.5)) rotate(90deg); opacity: 1; }
					50% { transform: translateY(55vh) translateX(var(--sway)) rotate(180deg); opacity: 0.8; }
					75% { transform: translateY(80vh) translateX(calc(var(--sway) * 0.3)) rotate(270deg); opacity: 0.5; }
					100% { transform: translateY(110vh) translateX(0) rotate(360deg); opacity: 0; }
				}
				@keyframes boardEffectRise {
					0% { transform: translateY(110vh) translateX(0) scale(0.8); opacity: 0; }
					10% { opacity: 1; }
					50% { transform: translateY(50vh) translateX(var(--sway)) scale(1); opacity: 1; }
					100% { transform: translateY(-10%) translateX(calc(var(--sway) * -0.5)) scale(1.1); opacity: 0; }
				}
				@keyframes boardBalloonRise {
					0% { transform: translateY(110vh) translateX(0) scale(0.6); opacity: 0; }
					5% { opacity: 1; transform: translateY(100vh) translateX(0) scale(1); }
					25% { transform: translateY(70vh) translateX(var(--sway)) scale(1); }
					50% { transform: translateY(40vh) translateX(calc(var(--sway) * -0.5)) scale(1.02); opacity: 1; }
					75% { transform: translateY(15vh) translateX(var(--sway)) scale(1); opacity: 0.8; }
					100% { transform: translateY(-15%) translateX(0) scale(0.95); opacity: 0; }
				}
				@keyframes boardHeartFloat {
					0% { transform: translateY(110vh) translateX(0) scale(0.5) rotate(0deg); opacity: 0; }
					10% { opacity: 1; transform: translateY(90vh) translateX(0) scale(1) rotate(0deg); }
					30% { transform: translateY(60vh) translateX(var(--sway)) scale(1.1) rotate(-10deg); }
					50% { transform: translateY(40vh) translateX(calc(var(--sway) * -0.7)) scale(0.9) rotate(10deg); opacity: 1; }
					70% { transform: translateY(20vh) translateX(var(--sway)) scale(1.05) rotate(-5deg); opacity: 0.7; }
					100% { transform: translateY(-10%) translateX(0) scale(0.8) rotate(0deg); opacity: 0; }
				}
				@keyframes boardFireworkBurst {
					0% { transform: translateY(100vh) scale(0); opacity: 0; }
					20% { transform: translateY(var(--burst-y)) scale(0.3); opacity: 1; }
					40% { transform: translateY(var(--burst-y)) scale(1); opacity: 1; }
					60% { transform: translateY(var(--burst-y)) translateX(var(--sway)) scale(1.2); opacity: 0.8; }
					80% { transform: translateY(calc(var(--burst-y) + 20vh)) translateX(calc(var(--sway) * 1.5)) scale(0.5); opacity: 0.3; }
					100% { transform: translateY(calc(var(--burst-y) + 40vh)) translateX(var(--sway)) scale(0); opacity: 0; }
				}
				@keyframes boardSnowFall {
					0% { transform: translateY(-5%) translateX(0) rotate(0deg); opacity: 0.8; }
					25% { transform: translateY(25vh) translateX(var(--sway)) rotate(90deg); opacity: 1; }
					50% { transform: translateY(50vh) translateX(calc(var(--sway) * -0.5)) rotate(180deg); opacity: 0.9; }
					75% { transform: translateY(75vh) translateX(var(--sway)) rotate(270deg); opacity: 0.7; }
					100% { transform: translateY(105vh) translateX(0) rotate(360deg); opacity: 0; }
				}
				@keyframes boardEffectTwinkle {
					0%, 100% { opacity: 0.1; transform: scale(0.6) rotate(0deg); }
					25% { opacity: 0.8; transform: scale(1) rotate(20deg); }
					50% { opacity: 1; transform: scale(1.3) rotate(0deg); }
					75% { opacity: 0.6; transform: scale(0.9) rotate(-20deg); }
				}
			`}</style>
			{particles.map((p) => {
				const isChar = !!p.char;
				let animName: string;
				if (effect === 'sparkles') animName = 'boardEffectTwinkle';
				else if (effect === 'balloons') animName = 'boardBalloonRise';
				else if (effect === 'hearts') animName = 'boardHeartFloat';
				else if (effect === 'fireworks') animName = 'boardFireworkBurst';
				else if (effect === 'snowflakes') animName = 'boardSnowFall';
				else if (isDown) animName = 'boardEffectFall';
				else animName = 'boardEffectRise';

				const style: React.CSSProperties & Record<string, any> = {
					position: 'absolute',
					left: `${p.x}%`,
					width: isChar ? 'auto' : `${p.size}px`,
					height: isChar ? 'auto' : effect === 'balloons' ? `${p.size * 1.2}px` : `${p.size}px`,
					fontSize: isChar ? `${p.size}px` : undefined,
					lineHeight: 1,
					animation: `${animName} ${p.duration}s ${p.delay}s infinite ease-in-out`,
					'--sway': `${(Math.random() > 0.5 ? 1 : -1) * p.swayAmount}px`,
				};

				// Position
				if (effect === 'fireworks') {
					style.bottom = '0';
					style['--burst-y'] = `${20 + Math.random() * 40}vh`;
				} else if (isDown) {
					style.top = '-5%';
				} else {
					style.bottom = '-5%';
				}

				// Shape-specific styling
				if (effect === 'balloons' && !isChar) {
					style.borderRadius = '50% 50% 50% 50% / 40% 40% 60% 60%';
					style.backgroundColor = p.color;
					style.boxShadow = `inset -${p.size / 6}px -${p.size / 6}px ${p.size / 3}px rgba(0,0,0,0.1), inset ${p.size / 8}px ${p.size / 8}px ${p.size / 4}px rgba(255,255,255,0.3)`;
				} else if (effect === 'confetti' && !isChar) {
					style.borderRadius = Math.random() > 0.5 ? '2px' : '50%';
					style.backgroundColor = p.color;
					style.width = `${p.size}px`;
					style.height = Math.random() > 0.5 ? `${p.size * 0.6}px` : `${p.size}px`;
				} else if (effect === 'fireworks' && !isChar) {
					style.borderRadius = '50%';
					style.backgroundColor = p.color;
					style.boxShadow = `0 0 ${p.size}px ${p.size / 2}px ${p.color}40`;
				} else if (!isChar) {
					style.borderRadius = '50%';
					style.backgroundColor = p.color;
				} else {
					style.backgroundColor = 'transparent';
				}

				return (
					<div key={p.id} style={style}>
						{isChar ? p.char : null}
					</div>
				);
			})}
		</div>
	);
};

export default memo(BoardEffects);
